import { apiService } from './api';
import {
  Invoice,
  ClientWithPlacements,
  ProjectWithPlacements,
  GenerateInvoiceRequest,
  OverrideAmountRequest,
  UpdateStatusRequest,
  InvoiceFilters,
} from '../types/invoice';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ClientBillingPlacement {
  placementId: string;
  orderNumber: string;
  orderDate: string | null;
  siteUrl: string;
  projectName: string;
  clientName?: string;
  submissionType: string | null;
  price: number;
  liveUrl: string | null;
  placedAt: string | null;
  paidAt?: string | null;
}

export interface ClientBillingMonth {
  month: string;
  monthNum: number;
  year: number;
  total: number;
  count: number;
}

export interface ClientBillingData {
  grandTotal: number;
  totalPlacements: number;
  monthlyTotals: ClientBillingMonth[];
  placements: ClientBillingPlacement[];
}

export interface ClientsWithPlacementsResponse {
  success: boolean;
  data: ClientWithPlacements[];
  month: number;
  year: number;
}

class InvoiceService {
  private baseUrl = '/invoices';

  /**
   * Get clients with completed placements for a specific month
   */
  async getClientsWithCompletedPlacements(month: number, year: number): Promise<ClientsWithPlacementsResponse> {
    const params = new URLSearchParams();
    params.append('month', month.toString());
    params.append('year', year.toString());

    const response = await apiService.get<ClientsWithPlacementsResponse>(
      `${this.baseUrl}/clients-with-completed-placements?${params.toString()}`
    );
    return response;
  }

  async getProjectsWithCompletedPlacements(
    clientId: string,
    month: number,
    year: number,
  ): Promise<ApiResponse<ProjectWithPlacements[]>> {
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    params.append('month', month.toString());
    params.append('year', year.toString());

    const response = await apiService.get<ApiResponse<ProjectWithPlacements[]>>(
      `${this.baseUrl}/projects-with-completed-placements?${params.toString()}`
    );
    return response;
  }

  /**
   * Get all invoices with optional filters
   */
  async getInvoices(filters: InvoiceFilters = {}): Promise<ApiResponse<Invoice[]>> {
    const params = new URLSearchParams();
    
    if (filters.clientId) params.append('clientId', filters.clientId);
    if (filters.projectId) params.append('projectId', filters.projectId);
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.year) params.append('year', filters.year.toString());
    if (filters.status) params.append('status', filters.status);

    const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;
    const response = await apiService.get<ApiResponse<Invoice[]>>(url);
    return response;
  }

  /**
   * Get billing for the logged-in client or agency — based on succeeded Stripe payments.
   */
  async getMyBilling(filters: { month?: number; year?: number; clientId?: string } = {}): Promise<ApiResponse<ClientBillingData>> {
    const params = new URLSearchParams();
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.year) params.append('year', filters.year.toString());
    if (filters.clientId) params.append('clientId', filters.clientId);

    const url = params.toString() ? `${this.baseUrl}/my?${params.toString()}` : `${this.baseUrl}/my`;
    const response = await apiService.get<ApiResponse<ClientBillingData>>(url);
    return response;
  }

  /**
   * Get invoice by ID
   */
  async getInvoice(id: string): Promise<ApiResponse<Invoice>> {
    const response = await apiService.get<ApiResponse<Invoice>>(`${this.baseUrl}/${id}`);
    return response;
  }

  /**
   * Generate invoice for a client and month
   */
  async generateInvoice(data: GenerateInvoiceRequest): Promise<ApiResponse<Invoice>> {
    const response = await apiService.post<ApiResponse<Invoice>>(
      `${this.baseUrl}/generate`,
      data
    );
    return response;
  }

  /**
   * Regenerate invoice with current completed placements
   */
  async regenerateInvoice(id: string): Promise<ApiResponse<Invoice>> {
    const response = await apiService.post<ApiResponse<Invoice>>(
      `${this.baseUrl}/${id}/regenerate`,
      {}
    );
    return response;
  }

  /**
   * Override invoice amount
   */
  async overrideAmount(id: string, data: OverrideAmountRequest): Promise<ApiResponse<Invoice>> {
    const response = await apiService.patch<ApiResponse<Invoice>>(
      `${this.baseUrl}/${id}/override-amount`,
      data
    );
    return response;
  }

  /**
   * Update invoice status
   */
  async updateStatus(id: string, data: UpdateStatusRequest): Promise<ApiResponse<Invoice>> {
    const response = await apiService.patch<ApiResponse<Invoice>>(
      `${this.baseUrl}/${id}/status`,
      data
    );
    return response;
  }

  /**
   * Update individual invoice item price
   */
  async updateInvoiceItemPrice(invoiceId: string, itemId: string, unitPrice: number): Promise<ApiResponse<Invoice>> {
    const response = await apiService.patch<ApiResponse<Invoice>>(
      `${this.baseUrl}/${invoiceId}/items/${itemId}`,
      { unitPrice }
    );
    return response;
  }

  /**
   * Delete invoice
   */
  async deleteInvoice(id: string): Promise<ApiResponse<void>> {
    const response = await apiService.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
    return response;
  }

  /**
   * Format invoice amount
   */
  formatAmount(amount: number | string | any): string {
    // Convert to number if it's a string or Prisma Decimal
    const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));
    
    // Check if it's a valid number
    if (isNaN(numAmount)) {
      return '$0.00';
    }
    
    return `$${numAmount.toFixed(2)}`;
  }

  /**
   * Get month name
   */
  getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || '';
  }

  /**
   * Get invoice period string
   */
  getInvoicePeriod(month: number, year: number): string {
    return `${this.getMonthName(month)} ${year}`;
  }
}

export const invoiceService = new InvoiceService();
